import { useEffect, useState } from 'react';
import { 
  Users, 
  Eye, 
  Globe, 
  Monitor, 
  Smartphone, 
  Tablet,
  TrendingUp,
  Calendar
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { getAnalyticsSummary } from '../../lib/api';

const StatCard = ({ icon: Icon, label, value, subValue, color = 'neutral' }) => {
  const colorClasses = {
    neutral: 'bg-neutral-100 text-neutral-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    purple: 'bg-purple-100 text-purple-700'
  };
  
  return (
    <div className="admin-card" data-testid={`analytics-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold text-neutral-900">{value}</p>
          <p className="text-neutral-500 text-sm">{label}</p>
          {subValue && <p className="text-xs text-neutral-400 mt-1">{subValue}</p>}
        </div>
      </div>
    </div>
  );
};

const ProgressBar = ({ label, value, total, color = 'bg-neutral-700' }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-neutral-700">{label}</span>
        <span className="text-neutral-500">{value}</span>
      </div>
      <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await getAnalyticsSummary();
        setAnalytics(res.data);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile': return Smartphone;
      case 'tablet': return Tablet;
      default: return Monitor;
    }
  };

  const maxPageViews = analytics?.top_pages?.reduce((max, p) => Math.max(max, p.views), 0) || 1;
  const maxCountryVisitors = analytics?.countries?.reduce((max, c) => Math.max(max, c.visitors), 0) || 1;

  return (
    <AdminLayout>
      <div data-testid="admin-analytics-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
            Analytics
          </h1>
          <p className="text-neutral-500 mt-2">Track your website visitors and engagement</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
          </div>
        ) : error ? (
          <div className="admin-card text-center py-8">
            <p className="text-red-600">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 text-neutral-600 underline"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard 
                icon={Users} 
                label="Total Visitors" 
                value={analytics?.total_visitors || 0}
                color="blue"
              />
              <StatCard 
                icon={Calendar} 
                label="Today's Visitors" 
                value={analytics?.today_visitors || 0}
                subValue="New visitors today"
                color="green"
              />
              <StatCard 
                icon={TrendingUp} 
                label="This Week" 
                value={analytics?.week_visitors || 0}
                subValue="Visitors in last 7 days"
                color="purple"
              />
              <StatCard 
                icon={Eye} 
                label="Total Page Views" 
                value={analytics?.total_pageviews || 0}
                color="neutral"
              />
            </div>

            {/* Daily Visitors Chart */}
            <div className="admin-card mb-8">
              <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                Daily Visitors (Last 7 Days)
              </h2>
              <div className="flex items-end justify-between gap-2 h-48" data-testid="daily-visitors-chart">
                {analytics?.daily_visitors?.map((day, index) => {
                  const maxVisitors = Math.max(...analytics.daily_visitors.map(d => d.visitors), 1);
                  const height = (day.visitors / maxVisitors) * 100;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <span className="text-sm font-medium text-neutral-700">{day.visitors}</span>
                      <div 
                        className="w-full bg-neutral-800 rounded-t-md transition-all duration-500 hover:bg-neutral-700"
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                      <span className="text-xs text-neutral-500">{day.day}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Top Pages */}
              <div className="admin-card">
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Top Pages
                </h2>
                <div data-testid="top-pages-list">
                  {analytics?.top_pages?.length > 0 ? (
                    analytics.top_pages.map((page, index) => (
                      <ProgressBar
                        key={index}
                        label={page.page === '/' ? 'Home' : page.page}
                        value={page.views}
                        total={maxPageViews}
                        color="bg-blue-600"
                      />
                    ))
                  ) : (
                    <p className="text-neutral-500 text-center py-4">No page data yet</p>
                  )}
                </div>
              </div>

              {/* Visitors by Country */}
              <div className="admin-card">
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <Globe className="inline w-5 h-5 mr-2" />
                  Visitors by Country
                </h2>
                <div data-testid="countries-list">
                  {analytics?.countries?.length > 0 ? (
                    analytics.countries.map((country, index) => (
                      <ProgressBar
                        key={index}
                        label={country.country || 'Unknown'}
                        value={country.visitors}
                        total={maxCountryVisitors}
                        color="bg-green-600"
                      />
                    ))
                  ) : (
                    <p className="text-neutral-500 text-center py-4">No location data yet</p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Devices */}
              <div className="admin-card">
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Devices
                </h2>
                <div className="space-y-4" data-testid="devices-list">
                  {analytics?.devices?.length > 0 ? (
                    analytics.devices.map((device, index) => {
                      const Icon = getDeviceIcon(device.device);
                      const total = analytics.devices.reduce((sum, d) => sum + d.visitors, 0);
                      const percentage = total > 0 ? Math.round((device.visitors / total) * 100) : 0;
                      return (
                        <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5 text-neutral-600" />
                            <span className="font-medium capitalize">{device.device || 'Unknown'}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-neutral-900">{device.visitors}</span>
                            <span className="text-neutral-500 text-sm ml-2">({percentage}%)</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-neutral-500 text-center py-4">No device data yet</p>
                  )}
                </div>
              </div>

              {/* Browsers */}
              <div className="admin-card">
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Browsers
                </h2>
                <div className="space-y-4" data-testid="browsers-list">
                  {analytics?.browsers?.length > 0 ? (
                    analytics.browsers.map((browser, index) => {
                      const total = analytics.browsers.reduce((sum, b) => sum + b.visitors, 0);
                      const percentage = total > 0 ? Math.round((browser.visitors / total) * 100) : 0;
                      return (
                        <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                          <span className="font-medium">{browser.browser || 'Unknown'}</span>
                          <div className="text-right">
                            <span className="font-bold text-neutral-900">{browser.visitors}</span>
                            <span className="text-neutral-500 text-sm ml-2">({percentage}%)</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-neutral-500 text-center py-4">No browser data yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Recent Visitors */}
            <div className="admin-card">
              <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                Recent Visitors
              </h2>
              <div className="overflow-x-auto" data-testid="recent-visitors-table">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-500">Location</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-500">Device</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-500">Browser</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-500">Pages</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-neutral-500">Last Visit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics?.recent_visitors?.length > 0 ? (
                      analytics.recent_visitors.map((visitor, index) => (
                        <tr key={index} className="border-b border-neutral-100 hover:bg-neutral-50">
                          <td className="py-3 px-4">
                            <span className="font-medium">{visitor.city || 'Unknown'}</span>
                            {visitor.country && visitor.country !== 'Unknown' && (
                              <span className="text-neutral-500 text-sm ml-1">({visitor.country})</span>
                            )}
                          </td>
                          <td className="py-3 px-4 capitalize">{visitor.device_type || 'Unknown'}</td>
                          <td className="py-3 px-4">{visitor.browser || 'Unknown'}</td>
                          <td className="py-3 px-4">{visitor.page_views}</td>
                          <td className="py-3 px-4 text-neutral-500 text-sm">
                            {new Date(visitor.last_visit).toLocaleString()}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-neutral-500">
                          No visitor data yet. Start tracking by visiting your website!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
